import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { usePageMeta } from "@/lib/meta";
import Reveal, { Overline } from "@/components/Reveal";
import { Field, RadioCards, inputCls } from "@/components/forms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "leading-your-team", label: "Leading Your Team" },
  { value: "facing-the-boardroom-and-the-public", label: "Facing the Boardroom and the Public" },
  { value: "deals-and-negotiations", label: "Deals and Negotiations" },
  { value: "crisis-and-difficult-moments", label: "Crisis and Difficult Moments" },
  { value: "not-sure", label: "Not sure yet" },
];

const SITUATION_OPTIONS = [
  { value: "investor-pitch", label: "I am preparing for an investor pitch" },
  { value: "high-stakes", label: "Another high-stakes communication situation" },
  { value: "general", label: "General development — no single deadline" },
];

const FORMAT_OPTIONS = [
  { value: "virtual", label: "Virtual" },
  { value: "in-person-austin", label: "In person in Austin" },
  { value: "either", label: "Either works" },
];

export default function Contact() {
  usePageMeta(
    "Request a Confidential Consultation | Credible Presence — Austin Executive Voice Consultant",
    "Request a confidential consultation with an Austin executive voice consultant. Executive communication coaching and investor pitch coaching, virtual or in person in Austin, TX. All inquiries are confidential."
  );

  const [params] = useSearchParams();
  const preselected = params.get("service");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role_org: "",
    service_interest: SERVICE_OPTIONS.some((o) => o.value === preselected) ? preselected : "",
    situation: "",
    challenge: "",
    format: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const valid =
    form.name.trim() &&
    /.+@.+\..+/.test(form.email) &&
    form.role_org.trim() &&
    form.service_interest &&
    form.situation &&
    form.challenge.trim() &&
    form.format;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, phone: form.phone || null });
      setSent(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page" className="bg-[#F7F5F1] pt-40 pb-24 sm:pt-48">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        {sent ? (
          <Reveal className="mx-auto max-w-2xl text-center">
            <div data-testid="contact-success" className="border border-[#2F5D8C]/30 bg-[#2E3038] px-8 py-16 sm:px-14">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#2F5D8C]/50">
                <Check size={22} className="text-[#E3D9C8]" />
              </div>
              <h1 className="mt-8 font-serif text-4xl tracking-tight text-[#F7F5F1]">Thank you.</h1>
              <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-[#C4BFB4]">
                Your request has been received and will be read personally. You can expect a confidential reply
                — this is not a sales call, and nothing about your inquiry is shared.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <Overline>Contact</Overline>
                <h1 className="mt-6 font-serif text-4xl leading-[1.08] tracking-tight text-[#26282E] sm:text-5xl">
                  Request a Confidential Consultation
                </h1>
                <p className="mt-8 text-base leading-relaxed text-[#5B5E66] sm:text-lg">
                  Tell me a little about who you are and what you are preparing for.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="mt-10 border-l-2 border-[#2F5D8C] bg-[#E3D9C8] p-6" data-testid="contact-reassurance">
                  <p className="font-serif text-lg italic leading-relaxed text-[#26282E]/85">
                    “All inquiries are confidential. This is not a sales call; it is a focused conversation about
                    whether coaching is the right fit.”
                  </p>
                </div>
                <ul className="mt-10 space-y-4 text-sm leading-relaxed text-[#5B5E66]">
                  <li className="flex gap-3"><span className="font-mono text-[#2F5D8C]">01</span> Your inquiry is read personally — never routed to a sales team.</li>
                  <li className="flex gap-3"><span className="font-mono text-[#2F5D8C]">02</span> You receive a candid assessment</li>
                  <li className="flex gap-3"><span className="font-mono text-[#2F5D8C]">03</span> Virtual sessions worldwide, or in person in Austin, Texas.</li>
                </ul>
              </Reveal>
            </div>

            <Reveal delay={0.1} className="lg:col-span-7">
              <form onSubmit={submit} className="card-shadow-lg space-y-7 rounded-md bg-white p-7 sm:p-10" data-testid="contact-form">
                <div className="grid gap-7 sm:grid-cols-2">
                  <Field label="Name" htmlFor="c-name">
                    <input id="c-name" data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
                  </Field>
                  <Field label="Email" htmlFor="c-email">
                    <input id="c-email" type="email" data-testid="contact-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </Field>
                </div>
                <div className="grid gap-7 sm:grid-cols-2">
                  <Field label="Phone number" htmlFor="c-phone" optional>
                    <input id="c-phone" type="tel" data-testid="contact-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(512) 555-0100" />
                  </Field>
                  <Field label="Role & organization" htmlFor="c-role">
                    <input id="c-role" data-testid="contact-role-input" className={inputCls} value={form.role_org} onChange={set("role_org")} placeholder="Founder & CEO, Acme Inc." />
                  </Field>
                </div>
                <Field label="Service interest" htmlFor="c-service">
                  <select id="c-service" data-testid="contact-service-select" className={inputCls} value={form.service_interest} onChange={set("service_interest")}>
                    <option value="" disabled>Select a service</option>
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="What are you preparing for?">
                  <RadioCards options={SITUATION_OPTIONS} value={form.situation} onChange={set("situation")} testid="contact-situation" />
                </Field>
                <Field label="Your main communication challenge" htmlFor="c-challenge">
                  <textarea id="c-challenge" rows={4} data-testid="contact-challenge-input" className={inputCls} value={form.challenge} onChange={set("challenge")} placeholder="In a sentence or two — what would you most want to change about how you sound or come across?" />
                </Field>
                <Field label="Preferred format">
                  <RadioCards options={FORMAT_OPTIONS} value={form.format} onChange={set("format")} testid="contact-format" />
                </Field>
                <button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  disabled={!valid || submitting}
                  className="group inline-flex w-full items-center justify-center gap-3 rounded-md bg-[#2F5D8C] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#F7F5F1] transition-colors duration-300 hover:bg-[#254A6E] disabled:cursor-not-allowed disabled:opacity-30 sm:w-auto"
                >
                  {submitting ? "Sending…" : "Request a Confidential Consultation"}
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A8D94]">
                  Confidential · No obligation · No sales follow-up sequences
                </p>
              </form>
            </Reveal>
          </div>
        )}
      </div>
    </div>
  );
}
